import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MapPin,
  Building2,
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronUp,
  RefreshCcw,
  Loader,
  X,
  Save,
  Layers,
  ArrowRight,
  ArrowLeft,
  Check
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import locationsService from '../services/locationsService';
import './LocationsPage.css';

const LocationsPage = () => {
  useTranslation();
  const toast = useToast();

  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCities, setExpandedCities] = useState({});
  const [expandedAreas, setExpandedAreas] = useState({});
  const [showUnassigned, setShowUnassigned] = useState({});
  
  // Modal states
  const [showCityModal, setShowCityModal] = useState(false);
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [showDistrictModal, setShowDistrictModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showPickDistrictsModal, setShowPickDistrictsModal] = useState(false);
  
  // Form states
  const [editingCity, setEditingCity] = useState(null);
  const [editingArea, setEditingArea] = useState(null);
  const [editingDistrict, setEditingDistrict] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignAreaId, setAssignAreaId] = useState('');
  // districtMode: 'area' = adding to area, 'city' = adding directly to city
  const [districtMode, setDistrictMode] = useState('city');
  const [pickAreaId, setPickAreaId] = useState(null);
  const [pickCityId, setPickCityId] = useState(null);
  const [selectedDistrictIds, setSelectedDistrictIds] = useState([]);
  
  const [cityForm, setCityForm] = useState({ arLabel: '', enLabel: '' });
  const [areaForm, setAreaForm] = useState({ arLabel: '', enLabel: '' });
  const [districtForm, setDistrictForm] = useState({ arLabel: '', enLabel: '' });
  const [submitting, setSubmitting] = useState(false);

  // Fetch all cities
  const fetchCities = useCallback(async () => {
    try {
      setLoading(true);
      const response = await locationsService.getAllCities();
      if (response.success) {
        setCities(response.data.cities || []);
      }
    } catch (error) {
      toast.error('فشل في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  const toggleCityExpansion = (cityId) => {
    setExpandedCities(prev => ({
      ...prev,
      [cityId]: !prev[cityId]
    }));
  };

  const toggleAreaExpansion = (areaId) => {
    setExpandedAreas(prev => ({
      ...prev,
      [areaId]: !prev[areaId]
    }));
  };

  const toggleUnassigned = (cityId) => {
    setShowUnassigned(prev => ({
      ...prev,
      [cityId]: !prev[cityId]
    }));
  };

  // City handlers
  const openAddCityModal = () => {
    setEditingCity(null);
    setCityForm({ arLabel: '', enLabel: '' });
    setShowCityModal(true);
  };

  const openEditCityModal = (city) => {
    setEditingCity(city);
    setCityForm({ arLabel: city.arLabel, enLabel: city.enLabel || '' });
    setShowCityModal(true);
  };

  const handleCitySubmit = async () => {
    if (!cityForm.arLabel.trim()) {
      toast.error('الرجاء إدخال اسم المدينة بالعربية');
      return;
    }
    try {
      setSubmitting(true);
      let response;
      if (editingCity) {
        response = await locationsService.updateCity(editingCity._id, cityForm.arLabel, cityForm.enLabel);
      } else {
        response = await locationsService.addCity(cityForm.arLabel, cityForm.enLabel);
      }
      if (response.success) {
        toast.success(editingCity ? 'تم تحديث المدينة بنجاح' : 'تم إضافة المدينة بنجاح');
        setShowCityModal(false);
        fetchCities();
      } else {
        toast.error(response.message || 'حدث خطأ');
      }
    } catch (error) {
      toast.error(error.message || 'حدث خطأ');
    } finally {
      setSubmitting(false);
    }
  };

  // Area handlers
  const openAddAreaModal = (cityId) => {
    setSelectedCityId(cityId);
    setEditingArea(null);
    setAreaForm({ arLabel: '', enLabel: '' });
    setShowAreaModal(true);
  };

  const openEditAreaModal = (cityId, area) => {
    setSelectedCityId(cityId);
    setEditingArea(area);
    setAreaForm({ arLabel: area.arLabel, enLabel: area.enLabel || '' });
    setShowAreaModal(true);
  };

  const handleAreaSubmit = async () => {
    if (!areaForm.arLabel.trim()) {
      toast.error('الرجاء إدخال اسم المنطقة بالعربية');
      return;
    }
    try {
      setSubmitting(true);
      let response;
      if (editingArea) {
        response = await locationsService.updateArea(selectedCityId, editingArea._id, areaForm.arLabel, areaForm.enLabel);
      } else {
        response = await locationsService.addArea(selectedCityId, areaForm.arLabel, areaForm.enLabel);
      }
      if (response.success) {
        toast.success(editingArea ? 'تم تحديث المنطقة بنجاح' : 'تم إضافة المنطقة بنجاح');
        setShowAreaModal(false);
        fetchCities();
      } else {
        toast.error(response.message || 'حدث خطأ');
      }
    } catch (error) {
      toast.error(error.message || 'حدث خطأ');
    } finally {
      setSubmitting(false);
    }
  };

  // District handlers
  const openAddDistrictModal = (areaId) => {
    setSelectedAreaId(areaId);
    setDistrictMode('area');
    setEditingDistrict(null);
    setDistrictForm({ arLabel: '', enLabel: '' });
    setShowDistrictModal(true);
  };

  const openAddDistrictToCityModal = (cityId) => {
    setSelectedCityId(cityId);
    setSelectedAreaId(null);
    setDistrictMode('city');
    setEditingDistrict(null);
    setDistrictForm({ arLabel: '', enLabel: '' });
    setShowDistrictModal(true);
  };

  const openEditDistrictModal = (areaId, district) => {
    setSelectedAreaId(areaId);
    setDistrictMode(areaId ? 'area' : 'city');
    setEditingDistrict(district);
    setDistrictForm({ arLabel: district.arLabel, enLabel: district.enLabel || '' });
    setShowDistrictModal(true);
  };

  const handleDistrictSubmit = async () => {
    if (!districtForm.arLabel.trim()) {
      toast.error('الرجاء إدخال اسم الحي بالعربية');
      return;
    }
    try {
      setSubmitting(true);
      let response;
      if (editingDistrict) {
        if (districtMode === 'area' && selectedAreaId) {
          response = await locationsService.updateDistrict(selectedAreaId, editingDistrict._id, districtForm.arLabel, districtForm.enLabel);
        } else {
          response = await locationsService.updateDistrictByCity(selectedCityId, editingDistrict._id, districtForm.arLabel, districtForm.enLabel);
        }
      } else {
        if (districtMode === 'area' && selectedAreaId) {
          response = await locationsService.addDistrict(selectedAreaId, districtForm.arLabel, districtForm.enLabel);
        } else {
          response = await locationsService.addDistrictToCity(selectedCityId, districtForm.arLabel, districtForm.enLabel);
        }
      }
      if (response.success) {
        toast.success(editingDistrict ? 'تم تحديث الحي بنجاح' : 'تم إضافة الحي بنجاح');
        setShowDistrictModal(false);
        fetchCities();
      } else {
        toast.error(response.message || 'حدث خطأ');
      }
    } catch (error) {
      toast.error(error.message || 'حدث خطأ');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete handlers
  const openDeleteModal = (type, ids) => {
    setDeleteTarget({ type, ...ids });
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      setSubmitting(true);
      let response;
      if (deleteTarget.type === 'city') {
        response = await locationsService.deleteCity(deleteTarget.cityId);
      } else if (deleteTarget.type === 'area') {
        response = await locationsService.deleteArea(deleteTarget.cityId, deleteTarget.areaId);
      } else if (deleteTarget.areaId) {
        response = await locationsService.deleteDistrict(deleteTarget.areaId, deleteTarget.districtId);
      } else {
        response = await locationsService.deleteDistrictByCity(deleteTarget.cityId, deleteTarget.districtId);
      }
      if (response.success) {
        const msgs = { city: 'تم حذف المدينة بنجاح', area: 'تم حذف المنطقة بنجاح', district: 'تم حذف الحي بنجاح' };
        toast.success(msgs[deleteTarget.type]);
        setShowDeleteModal(false);
        fetchCities();
      } else {
        toast.error(response.message || 'حدث خطأ');
      }
    } catch (error) {
      toast.error(error.message || 'حدث خطأ');
    } finally {
      setSubmitting(false);
    }
  };

  // Assign district to area
  const openAssignModal = (district, cityId) => {
    setAssignTarget({ district, cityId });
    setAssignAreaId(district.area || '');
    setShowAssignModal(true);
  };

  const handleAssign = async () => {
    try {
      setSubmitting(true);
      const response = await locationsService.assignDistrictArea(assignTarget.district._id, assignAreaId || null);
      if (response.success) {
        toast.success('تم تحديث تعيين الحي بنجاح');
        setShowAssignModal(false);
        fetchCities();
      } else {
        toast.error(response.message || 'حدث خطأ');
      }
    } catch (error) {
      toast.error(error.message || 'حدث خطأ');
    } finally {
      setSubmitting(false);
    }
  };

  // Pick existing districts for area
  const openPickDistrictsModal = (areaId, cityId) => {
    setPickAreaId(areaId);
    setPickCityId(cityId);
    setSelectedDistrictIds([]);
    setShowPickDistrictsModal(true);
  };

  const toggleDistrictSelection = (districtId) => {
    setSelectedDistrictIds(prev =>
      prev.includes(districtId)
        ? prev.filter(id => id !== districtId)
        : [...prev, districtId]
    );
  };

  const handleBulkAssign = async () => {
    if (selectedDistrictIds.length === 0) {
      toast.error('يجب اختيار حي واحد على الأقل');
      return;
    }
    try {
      setSubmitting(true);
      const response = await locationsService.bulkAssignDistricts(pickAreaId, selectedDistrictIds);
      if (response.success) {
        toast.success(response.message || 'تم تعيين الأحياء بنجاح');
        setShowPickDistrictsModal(false);
        fetchCities();
      } else {
        toast.error(response.message || 'حدث خطأ');
      }
    } catch (error) {
      toast.error(error.message || 'حدث خطأ');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassignFromArea = async (districtId) => {
    try {
      setSubmitting(true);
      const response = await locationsService.assignDistrictArea(districtId, null);
      if (response.success) {
        toast.success('تم إلغاء تعيين الحي من المنطقة');
        fetchCities();
      } else {
        toast.error(response.message || 'حدث خطأ');
      }
    } catch (error) {
      toast.error(error.message || 'حدث خطأ');
    } finally {
      setSubmitting(false);
    }
  };

  // Statistics
  const totalCities = cities.length;
  const totalAreas = cities.reduce((acc, city) => acc + (city.areas?.length || 0), 0);
  const totalDistricts = cities.reduce((acc, city) => acc + (city.districtsCount || 0), 0);

  return (
    <div className="locations-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>
            <MapPin size={28} />
            إدارة المدن والمناطق والأحياء
          </h1>
          <p>إضافة وتعديل المدن والمناطق والأحياء التي تظهر للمسوقين والعملاء</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={fetchCities}>
            <RefreshCcw size={18} />
            تحديث
          </button>
          <button className="btn btn-primary" onClick={openAddCityModal}>
            <Plus size={18} />
            إضافة مدينة
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="stats-row">
        <div className="stat-card">
          <MapPin size={24} />
          <div>
            <span className="stat-value">{totalCities}</span>
            <span className="stat-label">إجمالي المدن</span>
          </div>
        </div>
        <div className="stat-card">
          <Layers size={24} />
          <div>
            <span className="stat-value">{totalAreas}</span>
            <span className="stat-label">إجمالي المناطق</span>
          </div>
        </div>
        <div className="stat-card">
          <Building2 size={24} />
          <div>
            <span className="stat-value">{totalDistricts}</span>
            <span className="stat-label">إجمالي الأحياء</span>
          </div>
        </div>
      </div>

      {/* Cities List */}
      {loading ? (
        <div className="loading-state">
          <Loader size={40} className="spinner" />
          <p>جاري التحميل...</p>
        </div>
      ) : cities.length === 0 ? (
        <div className="empty-state">
          <MapPin size={64} />
          <h3>لا توجد مدن</h3>
          <p>ابدأ بإضافة مدينة جديدة</p>
          <button className="btn btn-primary" onClick={openAddCityModal}>
            <Plus size={18} />
            إضافة مدينة
          </button>
        </div>
      ) : (
        <div className="cities-list">
          {cities.map(city => (
            <div key={city._id} className="city-card">
              <div className="city-header" onClick={() => toggleCityExpansion(city._id)}>
                <div className="city-info">
                  <MapPin size={20} />
                  <div className="city-names">
                    <h3>{city.arLabel}</h3>
                    {city.enLabel && <span className="city-en">{city.enLabel}</span>}
                  </div>
                  <span className="districts-count">
                    {city.areasCount || 0} منطقة - {city.districtsCount || 0} حي
                  </span>
                </div>
                <div className="city-actions">
                  <button className="btn-icon" onClick={(e) => { e.stopPropagation(); openEditCityModal(city); }} title="تعديل">
                    <Edit2 size={16} />
                  </button>
                  <button className="btn-icon danger" onClick={(e) => { e.stopPropagation(); openDeleteModal('city', { cityId: city._id }); }} title="حذف">
                    <Trash2 size={16} />
                  </button>
                  <button className="expand-btn">
                    {expandedCities[city._id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                </div>
              </div>

              {expandedCities[city._id] && (
                <div className="areas-section">
                  <div className="areas-header">
                    <h4><Layers size={16} /> المناطق</h4>
                    <div className="areas-header-actions">
                      <button className="btn btn-sm btn-outline" onClick={() => openAddDistrictToCityModal(city._id)}>
                        <Plus size={14} />
                        إضافة حي
                      </button>
                      <button className="btn btn-sm btn-outline" onClick={() => openAddAreaModal(city._id)}>
                        <Plus size={14} />
                        إضافة منطقة
                      </button>
                    </div>
                  </div>

                  {/* Unassigned Districts */}
                  {city.unassignedDistricts && city.unassignedDistricts.length > 0 && (
                    <div className="unassigned-section">
                      <div className="unassigned-header" onClick={() => toggleUnassigned(city._id)}>
                        <div className="unassigned-info">
                          <Building2 size={16} />
                          <h5>أحياء بدون منطقة</h5>
                          <span className="districts-count-sm">{city.unassignedDistricts.length} حي</span>
                        </div>
                        <button className="expand-btn-sm">
                          {showUnassigned[city._id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                      </div>
                      {showUnassigned[city._id] && (
                        <div className="districts-grid">
                          {city.unassignedDistricts.map(district => (
                            <div key={district._id} className="district-item">
                              <div className="district-info">
                                <Building2 size={14} />
                                <span className="district-ar">{district.arLabel}</span>
                                {district.enLabel && <span className="district-en">({district.enLabel})</span>}
                              </div>
                              <div className="district-actions">
                                {city.areas && city.areas.length > 0 && (
                                  <button className="btn-icon sm" onClick={() => openAssignModal(district, city._id)} title="تعيين لمنطقة">
                                    <ArrowRight size={12} />
                                  </button>
                                )}
                                <button className="btn-icon sm" onClick={() => { setSelectedCityId(city._id); openEditDistrictModal(null, district); }} title="تعديل">
                                  <Edit2 size={12} />
                                </button>
                                <button className="btn-icon sm danger" onClick={() => openDeleteModal('district', { cityId: city._id, districtId: district._id })} title="حذف">
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {city.areas && city.areas.length > 0 ? (
                    <div className="areas-list">
                      {city.areas.map(area => (
                        <div key={area._id} className="area-card">
                          <div className="area-header" onClick={() => toggleAreaExpansion(area._id)}>
                            <div className="area-info">
                              <Layers size={16} />
                              <div className="area-names">
                                <h5>{area.arLabel}</h5>
                                {area.enLabel && <span className="area-en">{area.enLabel}</span>}
                              </div>
                              <span className="districts-count-sm">
                                {area.districtsCount || 0} حي
                              </span>
                            </div>
                            <div className="area-actions">
                              <button className="btn-icon sm" onClick={(e) => { e.stopPropagation(); openEditAreaModal(city._id, area); }} title="تعديل">
                                <Edit2 size={14} />
                              </button>
                              <button className="btn-icon sm danger" onClick={(e) => { e.stopPropagation(); openDeleteModal('area', { cityId: city._id, areaId: area._id }); }} title="حذف">
                                <Trash2 size={14} />
                              </button>
                              <button className="expand-btn-sm">
                                {expandedAreas[area._id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              </button>
                            </div>
                          </div>

                          {expandedAreas[area._id] && (
                            <div className="districts-section">
                              <div className="districts-header">
                                <h4>الأحياء</h4>
                                <div className="districts-header-actions">
                                  {city.unassignedDistricts && city.unassignedDistricts.length > 0 && (
                                    <button className="btn btn-sm btn-outline btn-pick" onClick={() => openPickDistrictsModal(area._id, city._id)}>
                                      <Check size={14} />
                                      اختيار أحياء موجودة
                                    </button>
                                  )}
                                  <button className="btn btn-sm btn-outline" onClick={() => openAddDistrictModal(area._id)}>
                                    <Plus size={14} />
                                    إضافة حي جديد
                                  </button>
                                </div>
                              </div>
                              {area.districts && area.districts.length > 0 ? (
                                <div className="districts-grid">
                                  {area.districts.map(district => (
                                    <div key={district._id} className="district-item">
                                      <div className="district-info">
                                        <Building2 size={14} />
                                        <span className="district-ar">{district.arLabel}</span>
                                        {district.enLabel && <span className="district-en">({district.enLabel})</span>}
                                      </div>
                                      <div className="district-actions">
                                        <button className="btn-icon sm warning" onClick={() => handleUnassignFromArea(district._id)} title="إلغاء التعيين من المنطقة" disabled={submitting}>
                                          <ArrowLeft size={12} />
                                        </button>
                                        <button className="btn-icon sm" onClick={() => openEditDistrictModal(area._id, district)} title="تعديل">
                                          <Edit2 size={12} />
                                        </button>
                                        <button className="btn-icon sm danger" onClick={() => openDeleteModal('district', { areaId: area._id, districtId: district._id })} title="حذف">
                                          <Trash2 size={12} />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="no-districts">لا توجد أحياء - أضف حي جديد أو اختر من الأحياء الموجودة</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-areas">لا توجد مناطق - يمكنك إضافة أحياء مباشرة أو إنشاء منطقة أولاً</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* City Modal */}
      {showCityModal && (
        <div className="modal-overlay" onClick={() => setShowCityModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCity ? 'تعديل المدينة' : 'إضافة مدينة جديدة'}</h3>
              <button className="close-btn" onClick={() => setShowCityModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>اسم المدينة بالعربية *</label>
                <input
                  type="text"
                  value={cityForm.arLabel}
                  onChange={(e) => setCityForm(prev => ({ ...prev, arLabel: e.target.value }))}
                  placeholder="مثال: الرياض"
                />
              </div>
              <div className="form-group">
                <label>اسم المدينة بالإنجليزية</label>
                <input
                  type="text"
                  value={cityForm.enLabel}
                  onChange={(e) => setCityForm(prev => ({ ...prev, enLabel: e.target.value }))}
                  placeholder="Example: Riyadh"
                  dir="ltr"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowCityModal(false)} disabled={submitting}>
                إلغاء
              </button>
              <button className="btn btn-primary" onClick={handleCitySubmit} disabled={submitting}>
                {submitting ? <Loader size={16} className="spinner" /> : <Save size={16} />}
                {editingCity ? 'حفظ التعديلات' : 'إضافة'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Area Modal */}
      {showAreaModal && (
        <div className="modal-overlay" onClick={() => setShowAreaModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingArea ? 'تعديل المنطقة' : 'إضافة منطقة جديدة'}</h3>
              <button className="close-btn" onClick={() => setShowAreaModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>اسم المنطقة بالعربية *</label>
                <input
                  type="text"
                  value={areaForm.arLabel}
                  onChange={(e) => setAreaForm(prev => ({ ...prev, arLabel: e.target.value }))}
                  placeholder="مثال: المنطقة الشمالية"
                />
              </div>
              <div className="form-group">
                <label>اسم المنطقة بالإنجليزية</label>
                <input
                  type="text"
                  value={areaForm.enLabel}
                  onChange={(e) => setAreaForm(prev => ({ ...prev, enLabel: e.target.value }))}
                  placeholder="Example: Northern Area"
                  dir="ltr"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowAreaModal(false)} disabled={submitting}>
                إلغاء
              </button>
              <button className="btn btn-primary" onClick={handleAreaSubmit} disabled={submitting}>
                {submitting ? <Loader size={16} className="spinner" /> : <Save size={16} />}
                {editingArea ? 'حفظ التعديلات' : 'إضافة'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* District Modal */}
      {showDistrictModal && (
        <div className="modal-overlay" onClick={() => setShowDistrictModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingDistrict ? 'تعديل الحي' : 'إضافة حي جديد'}</h3>
              <button className="close-btn" onClick={() => setShowDistrictModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>اسم الحي بالعربية *</label>
                <input
                  type="text"
                  value={districtForm.arLabel}
                  onChange={(e) => setDistrictForm(prev => ({ ...prev, arLabel: e.target.value }))}
                  placeholder="مثال: النرجس"
                />
              </div>
              <div className="form-group">
                <label>اسم الحي بالإنجليزية</label>
                <input
                  type="text"
                  value={districtForm.enLabel}
                  onChange={(e) => setDistrictForm(prev => ({ ...prev, enLabel: e.target.value }))}
                  placeholder="Example: Al Narjis"
                  dir="ltr"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDistrictModal(false)} disabled={submitting}>
                إلغاء
              </button>
              <button className="btn btn-primary" onClick={handleDistrictSubmit} disabled={submitting}>
                {submitting ? <Loader size={16} className="spinner" /> : <Save size={16} />}
                {editingDistrict ? 'حفظ التعديلات' : 'إضافة'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>تأكيد الحذف</h3>
              <button className="close-btn" onClick={() => setShowDeleteModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>
                {deleteTarget?.type === 'city' 
                  ? 'هل أنت متأكد من حذف هذه المدينة؟ سيتم حذف جميع المناطق والأحياء التابعة لها.'
                  : deleteTarget?.type === 'area'
                  ? 'هل أنت متأكد من حذف هذه المنطقة؟ سيتم حذف جميع الأحياء التابعة لها.'
                  : 'هل أنت متأكد من حذف هذا الحي؟'}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDeleteModal(false)} disabled={submitting}>
                إلغاء
              </button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={submitting}>
                {submitting ? <Loader size={16} className="spinner" /> : <Trash2 size={16} />}
                حذف
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign District to Area Modal */}
      {showAssignModal && assignTarget && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>تعيين الحي لمنطقة</h3>
              <button className="close-btn" onClick={() => setShowAssignModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>تعيين حي <strong>{assignTarget.district.arLabel}</strong> لمنطقة:</p>
              <div className="form-group">
                <label>اختر المنطقة</label>
                <select
                  value={assignAreaId}
                  onChange={(e) => setAssignAreaId(e.target.value)}
                  className="form-select"
                >
                  <option value="">بدون منطقة</option>
                  {(cities.find(c => c._id === assignTarget.cityId)?.areas || []).map(area => (
                    <option key={area._id} value={area._id}>{area.arLabel}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowAssignModal(false)} disabled={submitting}>
                إلغاء
              </button>
              <button className="btn btn-primary" onClick={handleAssign} disabled={submitting}>
                {submitting ? <Loader size={16} className="spinner" /> : <Save size={16} />}
                تعيين
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pick Districts Modal */}
      {showPickDistrictsModal && pickAreaId && pickCityId && (
        <div className="modal-overlay" onClick={() => setShowPickDistrictsModal(false)}>
          <div className="modal-content modal-pick" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>اختيار أحياء للمنطقة</h3>
              <button className="close-btn" onClick={() => setShowPickDistrictsModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {(() => {
                const city = cities.find(c => c._id === pickCityId);
                const area = city?.areas?.find(a => a._id === pickAreaId);
                const unassigned = city?.unassignedDistricts || [];
                return (
                  <>
                    <p className="pick-info">
                      اختر الأحياء التي تريد إضافتها لمنطقة <strong>{area?.arLabel}</strong>
                    </p>
                    {unassigned.length > 0 ? (
                      <div className="pick-districts-list">
                        <label className="pick-select-all">
                          <input
                            type="checkbox"
                            checked={selectedDistrictIds.length === unassigned.length}
                            onChange={() => {
                              if (selectedDistrictIds.length === unassigned.length) {
                                setSelectedDistrictIds([]);
                              } else {
                                setSelectedDistrictIds(unassigned.map(d => d._id));
                              }
                            }}
                          />
                          <span>تحديد الكل ({unassigned.length})</span>
                        </label>
                        <div className="pick-districts-grid">
                          {unassigned.map(district => (
                            <label key={district._id} className="pick-district-item">
                              <input
                                type="checkbox"
                                checked={selectedDistrictIds.includes(district._id)}
                                onChange={() => toggleDistrictSelection(district._id)}
                              />
                              <span className="pick-district-name">{district.arLabel}</span>
                              {district.enLabel && <span className="pick-district-en">({district.enLabel})</span>}
                            </label>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="no-districts">لا توجد أحياء غير معينة في هذه المدينة</p>
                    )}
                    {selectedDistrictIds.length > 0 && (
                      <div className="pick-summary">
                        تم اختيار <strong>{selectedDistrictIds.length}</strong> حي
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowPickDistrictsModal(false)} disabled={submitting}>
                إلغاء
              </button>
              <button className="btn btn-primary" onClick={handleBulkAssign} disabled={submitting || selectedDistrictIds.length === 0}>
                {submitting ? <Loader size={16} className="spinner" /> : <Check size={16} />}
                إضافة المحدد ({selectedDistrictIds.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationsPage;
